/* eslint-disable import/no-default-export, import/order, max-lines, max-lines-per-function, no-nested-ternary, react/no-unescaped-entities, react/prop-types */
import type { Ref } from "react";
import { useRef, useState, useEffect, useImperativeHandle } from "react";
import { REDRAW_NOTES_MAX_LENGTH } from "common/redraw-constants";
import { isMediumDeviceAndUp } from "common/utils/browser";
import { submitDtfEvent } from "common/utils/mixpanel/submit-dtf-events";
import { cn } from "tailwind-variants";
import CommonModal from "common/vue/components/CommonModal.react.vue";
import Tooltip from "common/vue/components/Tooltip.react.vue";
import { EventBus, Events } from "jiffy-store";
import debounce from "lodash.debounce";
import CustomNotes from "./ArtworkQualityConfigPanel/components/CustomNotes.vue";
import RedrawInformation from "./Modals/RedrawInformation.vue";
import PinningSessionEntry from "./PinningSession/PinningSessionEntry.vue";
import { useDtfStore } from "./store";
import type { Standard, Sticker } from "./types";
import "./RedrawNotes.scss";

export type RedrawNotesHandle = {
  focusTextarea: () => void;
};

type RedrawNotesProps = {
  ref?: Ref<RedrawNotesHandle>;
  index?: number;
  enhancementNote?: string;
  isMultiUpload?: boolean;
  disabled?: boolean;
  onLearnMore?: (() => void) | null;
};

type DtfStoreSlice = {
  isStandardArtwork: boolean;
  enableAbAiMonetization: boolean;
  showDesignServicesDeliveryBuffer: boolean;
  isPinBasedRedrawEnabled: boolean;
  artworks: (Standard.Artwork | Sticker.Artwork)[];
  updateArtworkValues: (args: { index: number; enhancementNote: string }) => void;
  getArtworksPricing: () => void;
  updatePdpDates: () => void;
};

function RedrawNotes({
  ref,
  index = 0,
  enhancementNote = undefined,
  isMultiUpload = false,
  disabled = false,
  onLearnMore = null
}: RedrawNotesProps) {
  const [note, setNote] = useState("");
  const [isOpenRedrawInformationModal, setIsOpenRedrawInformationModal] = useState(false);
  const isMobile = !isMediumDeviceAndUp();
  const pricingRefreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [previousNoteValue, setPreviousNoteValue] = useState("");

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const redrawInformationTooltipRef = useRef<{ hideTooltip: () => void }>(null);
  const redrawInformationModalRef = useRef<{ close: () => void }>(null);

  const dtfStore = useDtfStore() as unknown as DtfStoreSlice;

  function showPinningSessionEntry() {
    const artwork = dtfStore.artworks[index];
    return (
      artwork &&
      (dtfStore.isPinBasedRedrawEnabled ||
        ("pinningSessionId" in artwork && artwork.pinningSessionId))
    );
  }

  const debouncedUpdatePdpDates = useRef(
    debounce(() => {
      dtfStore.updatePdpDates();
    }, 500)
  ).current;

  const debouncedSubmitNoteChangedEvent = useRef(
    debounce(noteValue => {
      submitDtfEvent("dtf_need_artist_edits_note_changed", {
        page: "pdp",
        artwork_index: index,
        note_value: noteValue,
        note_length: noteValue.length
      });
    }, 500)
  ).current;

  function saveNote(e: any) {
    const noteValue = (e.target.value ?? "").trim();
    dtfStore.updateArtworkValues({
      index,
      enhancementNote: noteValue
    });
    if (noteValue !== previousNoteValue) {
      debouncedSubmitNoteChangedEvent(noteValue);
    }
    if (dtfStore.showDesignServicesDeliveryBuffer) {
      const hadContent = !!previousNoteValue;
      const hasContent = !!noteValue;
      if (hasContent || (hadContent && !hasContent)) {
        debouncedUpdatePdpDates();
      }
    }
    setPreviousNoteValue(noteValue);
    // Debounce pricing refresh to avoid excessive API calls
    if (pricingRefreshTimeoutRef.current) clearTimeout(pricingRefreshTimeoutRef.current);
    pricingRefreshTimeoutRef.current = setTimeout(() => {
      dtfStore.getArtworksPricing();
    }, 500);
  }

  function openRedrawInformationModal() {
    submitDtfEvent("redraw_information_modal_opened", {
      page: "pdp"
    });
    if (isMobile) {
      setIsOpenRedrawInformationModal(true);
    }
  }

  function closeRedrawInformationModal() {
    redrawInformationModalRef.current?.close();
    setIsOpenRedrawInformationModal(false);
  }

  function closeRedrawInformationTooltip() {
    redrawInformationTooltipRef.current?.hideTooltip();
  }

  function focusTextarea() {
    if (textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center"
      });
    }
  }

  useImperativeHandle(ref, () => ({
    focusTextarea
  }));

  useEffect(() => {
    setNote(enhancementNote ?? "");
    setPreviousNoteValue(enhancementNote ?? "");

    const handleItemsAdded = () => {
      setNote("");
    };

    EventBus.on(Events.ITEMS_ADDED_TO_CART, handleItemsAdded);

    return () => {
      EventBus.removeEventListener(Events.ITEMS_ADDED_TO_CART, handleItemsAdded);
      if (debouncedUpdatePdpDates) {
        debouncedUpdatePdpDates.cancel();
      }
      if (debouncedSubmitNoteChangedEvent) {
        debouncedSubmitNoteChangedEvent.flush();
        debouncedSubmitNoteChangedEvent.cancel();
      }
      if (pricingRefreshTimeoutRef.current) {
        clearTimeout(pricingRefreshTimeoutRef.current);
      }
    };
  }, [enhancementNote]);

  return (
    <>
      {showPinningSessionEntry() ? (
        <PinningSessionEntry
          className={cn("scope-aee006ff", {
            "pt-5": !dtfStore.isStandardArtwork,
            "mb-5": isMultiUpload
          })}
          index={index}
          artwork={dtfStore.artworks[index]}
          data-testid="pinning-session-entry"
        />
      ) : dtfStore.isStandardArtwork && dtfStore.enableAbAiMonetization ? (
        <CustomNotes
          className="scope-aee006ff"
          modelValue={note}
          onUpdateModelValue={(val: any) => {
            setNote(val);
          }}
          disabled={disabled}
          onLearnMore={onLearnMore}
          index={index}
          onInput={saveNote}
          data-testid="custom-notes"
        />
      ) : (
        <div
          className={cn("redraw-notes scope-aee006ff", {
            "redraw-notes--multi-upload": isMultiUpload,
            "redraw-notes--standard-artwork": dtfStore.isStandardArtwork
          })}
        >
          <label
            className="redraw-notes__label scope-aee006ff"
            htmlFor={`redraw-note-${index}`}
            data-testid="redraw-notes-label"
          >
            <span className="redraw-notes__label-text scope-aee006ff">
              <span className="scope-aee006ff">
                <strong className="scope-aee006ff">Free</strong> optional design editing included
              </span>
              <span className="scope-aee006ff">
                (a <strong className="scope-aee006ff">$10</strong> value)!
              </span>
            </span>
            <Tooltip
              className="scope-aee006ff"
              ref={redrawInformationTooltipRef}
              disabled={isMobile}
              trigger="click"
              position="bottom-right"
              slots={{
                target: () => (
                  <button
                    className={cn("redraw-notes__how-it-works scope-aee006ff", {
                      "redraw-notes__how-it-works--standard-artwork": dtfStore.isStandardArtwork
                    })}
                    type="button"
                    onClick={openRedrawInformationModal}
                    data-testid="redraw-notes-how-it-works"
                  >
                    {" "}
                    How it works{" "}
                  </button>
                ),

                content: () => (
                  <RedrawInformation
                    className="scope-aee006ff"
                    isMobile={false}
                    onClose={closeRedrawInformationTooltip}
                    data-testid="redraw-information"
                  />
                )
              }}
              data-testid="tooltip"
            />
          </label>
          <textarea
            className="redraw-notes__textarea scope-aee006ff"
            id={`redraw-note-${index}`}
            ref={textareaRef}
            value={note}
            disabled={disabled}
            onChange={e => {
              setNote(e.target.value);
              saveNote(e);
            }}
            maxLength={REDRAW_NOTES_MAX_LENGTH}
            placeholder="e.g. Keep the white inside of the text. Change the letters of the logo to orange."
            data-testid="redraw-notes-textarea"
          />

          {dtfStore.showDesignServicesDeliveryBuffer ? (
            <label
              className="redraw-notes__note redraw-notes__note--delivery scope-aee006ff"
              data-testid="redraw-notes-note"
            >
              {" "}
              Design Services add 1 day to delivery.{" "}
            </label>
          ) : null}
        </div>
      )}
      {isMobile && isOpenRedrawInformationModal ? (
        <CommonModal
          className="scope-aee006ff"
          ref={redrawInformationModalRef}
          compact={true}
          hideCloseButton={true}
          bottomAligned={true}
          onClose={closeRedrawInformationModal}
          data-testid="common-modal"
        >
          <RedrawInformation
            className="scope-aee006ff"
            isMobile={true}
            onClose={closeRedrawInformationModal}
            data-testid="redraw-information-1"
          />
        </CommonModal>
      ) : null}
    </>
  );
}

RedrawNotes.displayName = "RedrawNotes";

export default RedrawNotes;
